import React, { useMemo } from 'react';
import {
  ShoppingCart,
  Package,
  Truck,
  AlertTriangle,
  ClipboardList,
  Leaf,
  BarChart3,
  Calendar,
  Wrench,
  ShieldCheck,
  Gavel,
  ArrowRight
} from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { format, subMonths, isSameMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import DataTable from './DataTable';
import Badge from './common/Badge';
import { useAppContext } from '../context/AppContext';
import { useConstruction } from '../context/ConstructionContext';
import { useModuleSettings } from '../context/ModuleSettingsContext';
import { formatIndex } from '../construction/money';
import { HardHat } from 'lucide-react';
import {
  formatCurrency,
  formatMoney,
  formatDate,
  getStatusText,
  getStatusTone,
  calculateTotalInventoryValue
} from '../utils/helpers';
import { t } from '../i18n';

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  return new Date(`${value}T00:00:00`);
};

const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(Number(value || 0))));

const average = (values) => {
  const valid = values.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (valid.length === 0) return 0;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const Dashboard = () => {
  const {
    currentUser,
    purchaseOrders,
    purchaseRequisitions,
    parks,
    inventory,
    locations,
    shipments,
    suppliers,
    movements,
    auditTrail,
    tenders,
    projectTimelines,
    renewableProjects,
    renewablePermits,
    renewablePermitAlerts,
    findCostCenterById,
    findAreaById,
    findParkById,
    projectFinancialOverview,
    setActivePage,
    preferences,
    updatePreferences
  } = useAppContext();

  const { getSummary, summaries: constructionSummaries, portfolio: constructionPortfolio, constructionAlerts } = useConstruction();
  const { isModuleActive } = useModuleSettings();
  const constructionEnabled = isModuleActive('construction-module');
  const isOfficeStaff = currentUser?.role === 'office_staff';
  const displayCurrency = preferences?.displayCurrency === 'CLP' ? 'CLP' : 'USD';
  const fxUsdClp = Number(preferences?.fxUsdClp || 950);

  const convertAmount = (amount, fromCurrency, toCurrency) => {
    const value = Number(amount || 0);
    const from = fromCurrency === 'CLP' ? 'CLP' : 'USD';
    const to = toCurrency === 'CLP' ? 'CLP' : 'USD';
    if (from === to) return value;
    if (!Number.isFinite(fxUsdClp) || fxUsdClp <= 0) return value;
    if (from === 'CLP' && to === 'USD') return value / fxUsdClp;
    if (from === 'USD' && to === 'CLP') return value * fxUsdClp;
    return value;
  };

  const formatDisplayMoney = (amount) => formatMoney(amount, displayCurrency);

  const accessibleParkIds = useMemo(() => {
    if (!isOfficeStaff || !currentUser) return new Set();
    const ids = parks
      .filter((park) => {
        const team = park.team || {};
        return (
          team.projectManagerId === currentUser.id ||
          (team.administrators || []).includes(currentUser.id) ||
          (team.areaManagers || []).includes(currentUser.id) ||
          (team.fieldTeam || []).includes(currentUser.id)
        );
      })
      .map((park) => park.id);
    return new Set(ids);
  }, [isOfficeStaff, parks, currentUser]);

  const requisitionsByUser = useMemo(() => {
    if (!isOfficeStaff || !currentUser) return [];
    return purchaseRequisitions.filter((req) => req.requesterId === currentUser.id);
  }, [isOfficeStaff, purchaseRequisitions, currentUser]);

  const linkedOrderIds = useMemo(() => {
    if (!isOfficeStaff) return new Set();
    return new Set(
      requisitionsByUser.map((req) => req.linkedOrderId).filter(Boolean)
    );
  }, [isOfficeStaff, requisitionsByUser]);

  const visibleOrders = useMemo(() => {
    if (!isOfficeStaff) return purchaseOrders;
    return purchaseOrders.filter((order) => {
      const areaParkId = order.areaId ? findAreaById(order.areaId)?.parkId : null;
      const orderParkId = order.parkId || areaParkId;
      const belongsToPark = accessibleParkIds.size > 0 && accessibleParkIds.has(orderParkId);
      const linkedToUser = linkedOrderIds.has(order.id);
      const createdByUser = order.createdBy === currentUser?.id;
      return belongsToPark || linkedToUser || createdByUser;
    });
  }, [isOfficeStaff, purchaseOrders, accessibleParkIds, linkedOrderIds, findAreaById, currentUser]);

  const ordersSource = isOfficeStaff ? visibleOrders : purchaseOrders;

  const totalOrders = ordersSource.length;
  const pendingOrders = ordersSource.filter(
    (order) => order.status && order.status.toString().startsWith('pendiente')
  ).length;
  const shipmentsInTransit = shipments.filter((shipment) => shipment.status !== 'recepcionado').length;
  const averageCompliance = suppliers.length
    ? Math.round(
        suppliers.reduce((sum, supplier) => sum + (supplier.complianceScore || 0), 0) /
          suppliers.length
      )
    : 0;

  const totalInventoryValue = useMemo(
    () => calculateTotalInventoryValue(inventory, locations),
    [inventory, locations]
  );

  const nowDate = new Date();
  const ordersThisMonth = ordersSource.filter((order) =>
    isSameMonth(parseDate(order.createdDate), nowDate)
  );

  const orderValueInDisplayCurrency = (order) =>
    convertAmount(order.totalValue || 0, order.currency || 'CLP', displayCurrency);

  const monthlyNational = ordersThisMonth
    .filter((order) => order.type === 'nacional')
    .reduce((sum, order) => sum + orderValueInDisplayCurrency(order), 0);

  const monthlyInternational = ordersThisMonth
    .filter((order) => order.type !== 'nacional')
    .reduce((sum, order) => sum + orderValueInDisplayCurrency(order), 0);

  const monthlyTrend = useMemo(() => {
    const months = Array.from({ length: 6 }).map((_, index) => {
      const monthDate = subMonths(nowDate, 5 - index);
      const label = format(monthDate, 'MMM', { locale: es });
      const monthOrders = ordersSource.filter((order) =>
        isSameMonth(parseDate(order.createdDate), monthDate)
      );
      const national = monthOrders
        .filter((order) => order.type === 'nacional')
        .reduce((sum, order) => sum + orderValueInDisplayCurrency(order), 0);
      const international = monthOrders
        .filter((order) => order.type !== 'nacional')
        .reduce((sum, order) => sum + orderValueInDisplayCurrency(order), 0);

      return { label: label.toUpperCase(), national, international };
    });

    return {
      labels: months.map((month) => month.label),
      datasets: [
        {
          label: t('Compras Nacionales'),
          data: months.map((month) => month.national),
          backgroundColor: '#58B09C',
          borderRadius: 10,
          maxBarThickness: 28
        },
        {
          label: t('Compras Internacionales'),
          data: months.map((month) => month.international),
          backgroundColor: '#1A2E45',
          borderRadius: 10,
          maxBarThickness: 28
        }
      ]
    };
  }, [ordersSource, nowDate, displayCurrency, fxUsdClp]);

  const dashboardExecutive = preferences?.dashboardExecutive || {};
  const selectedParkId = dashboardExecutive.parkId || 'all';
  const selectedSections = useMemo(() => {
    const sections = Array.isArray(dashboardExecutive.sections) ? dashboardExecutive.sections : [];
    return new Set(sections);
  }, [dashboardExecutive.sections]);

  const sectionCatalog = useMemo(
    () => [
      { id: 'development', label: t('Desarrollo'), weight: 30, icon: Leaf },
      { id: 'operations', label: t('Operaciones'), weight: 20, icon: Truck },
      { id: 'project_management', label: t('Project Mgmt'), weight: 20, icon: Calendar },
      { id: 'procurement', label: t('Procurement'), weight: 15, icon: ShoppingCart },
      { id: 'construction', label: t('Construcción'), weight: 10, icon: Wrench },
      { id: 'cost_control', label: t('Control costos'), weight: 5, icon: BarChart3 }
    ],
    []
  );

  const resolveParkId = (entity) => {
    if (!entity) return null;
    if (entity.parkId) return entity.parkId;
    if (entity.areaId) return findAreaById(entity.areaId)?.parkId || null;
    return null;
  };

  const executiveParks = useMemo(() => {
    if (!isOfficeStaff) return parks;
    if (accessibleParkIds.size === 0) return [];
    return parks.filter((park) => accessibleParkIds.has(park.id));
  }, [parks, isOfficeStaff, accessibleParkIds]);

  const executiveProjects = useMemo(() => {
    const today = new Date();

    const tenderClosedStatuses = new Set(['adjudicada', 'cancelada', 'awarded', 'closed', 'cerrada']);
    const openRequisitionStatuses = new Set([
      'pendiente_area',
      'pendiente_controller',
      'pendiente_compra',
      'en_licitacion'
    ]);
    const approvedOrderStatuses = new Set(['aprobada', 'recibida', 'enviada']);

    return executiveParks.map((park) => {
      const renewableProject = renewableProjects.find((proj) => proj.parkId === park.id) || null;
      const renewableProjectId = renewableProject?.id || null;

      const permitsForProject = renewableProjectId
        ? renewablePermits.filter((permit) => permit.projectId === renewableProjectId)
        : [];
      const permitsAvg = permitsForProject.length > 0 ? average(permitsForProject.map((p) => p.progress)) : 0;
      const criticalAlerts = renewableProjectId
        ? renewablePermitAlerts.filter(
            (alert) => alert.projectId === renewableProjectId && alert.priority === 'alta'
          ).length
        : 0;
      const developmentProgress = clampPercent(average([renewableProject?.rtbProgress, permitsAvg]));

      const timeline = projectTimelines.find((item) => item.parkId === park.id) || null;
      const tasks = timeline?.tasks || [];
      const timelineProgress = tasks.length > 0 ? clampPercent(average(tasks.map((task) => task.progress))) : 0;
      const overdueTasks = tasks.filter((task) => {
        if (!task?.end) return false;
        const end = parseDate(task.end);
        return end && end < today && Number(task.progress || 0) < 100;
      }).length;
      const projectManagementProgress = timelineProgress;

      const constructionTasks = tasks.filter((task) => {
        const partida = String(task.partida || '').toLowerCase();
        const owner = String(task.owner || '').toLowerCase();
        return partida.includes('constru') || owner.includes('epc') || owner.includes('constru');
      });
      const constructionSummary = constructionEnabled ? getSummary(park.id) : null;
      const hasConstructionData = Boolean(constructionSummary && constructionSummary.items.length > 0);
      const constructionEvm = hasConstructionData ? constructionSummary.evm.project : null;
      const constructionProgress = hasConstructionData
        ? clampPercent(constructionEvm.actualProgress)
        : constructionTasks.length > 0 ? clampPercent(average(constructionTasks.map((task) => task.progress))) : timelineProgress;

      const requisitionsForPark = purchaseRequisitions.filter((req) => resolveParkId(req) === park.id);
      const openRequisitions = requisitionsForPark.filter((req) => openRequisitionStatuses.has(req.status)).length;
      const convertedRequisitions = requisitionsForPark.filter((req) => req.status === 'convertida').length;
      const requisitionProgress =
        requisitionsForPark.length > 0 ? (convertedRequisitions / requisitionsForPark.length) * 100 : 0;

      const ordersForPark = purchaseOrders.filter((order) => resolveParkId(order) === park.id && order.status !== 'rechazada');
      const pendingOrdersForPark = ordersForPark.filter((order) =>
        order.status && order.status.toString().startsWith('pendiente')
      ).length;
      const approvedOrdersForPark = ordersForPark.filter((order) => approvedOrderStatuses.has(order.status)).length;
      const ordersProgress = ordersForPark.length > 0 ? (approvedOrdersForPark / ordersForPark.length) * 100 : 0;

      const tendersForPark = tenders.filter((tender) => {
        if (tender.parkId) return tender.parkId === park.id;
        if (tender.relatedRequisitionId) {
          const req = purchaseRequisitions.find((r) => r.id === tender.relatedRequisitionId);
          return resolveParkId(req) === park.id;
        }
        return false;
      });
      const openTenders = tendersForPark.filter((tender) => !tenderClosedStatuses.has(tender.status)).length;
      const tendersProgress = tendersForPark.length > 0 ? ((tendersForPark.length - openTenders) / tendersForPark.length) * 100 : 0;

      const procurementProgress = clampPercent(average([requisitionProgress, ordersProgress, tendersProgress]));

      const supplierIdsForPark = new Set(ordersForPark.map((order) => order.supplierId).filter(Boolean));
      const suppliersForPark = suppliers.filter((supplier) => supplierIdsForPark.has(supplier.id));
      const complianceAvg =
        suppliersForPark.length > 0 ? clampPercent(average(suppliersForPark.map((supplier) => supplier.complianceScore))) : 0;
      const expiredCerts = suppliersForPark.reduce((sum, supplier) => {
        const certs = Array.isArray(supplier.certifications) ? supplier.certifications : [];
        return (
          sum +
          certs.filter((cert) => cert.validUntil && new Date(`${cert.validUntil}T00:00:00`) < today).length
        );
      }, 0);
      const suppliersProgress = complianceAvg;

      const shipmentsForPark = shipments.filter((shipment) => resolveParkId(shipment) === park.id);
      const receivedShipments = shipmentsForPark.filter((shipment) => shipment.status === 'recepcionado').length;
      const operationsProgress =
        shipmentsForPark.length > 0 ? clampPercent((receivedShipments / shipmentsForPark.length) * 100) : 0;

      const financial = projectFinancialOverview.find((summary) => summary.parkId === park.id) || null;
      const budgetCurrency = financial?.currency || 'USD';
      const budget = convertAmount(financial?.totalBudget || 0, budgetCurrency, displayCurrency);
      const committed = convertAmount(financial?.totalCommitted || 0, budgetCurrency, displayCurrency);
      const invoiced = convertAmount(financial?.totalInvoiced || 0, budgetCurrency, displayCurrency);
      const budgetUsage = budget > 0 ? clampPercent((committed / budget) * 100) : 0;

      const dataCompleteness = clampPercent(
        (Number(Boolean(renewableProject)) +
          Number(permitsForProject.length > 0) +
          Number(Boolean(timeline)) +
          Number(Boolean(financial && financial.totalBudget > 0)) +
          Number(requisitionsForPark.length > 0) +
          Number(ordersForPark.length > 0)) *
          (100 / 6)
      );
      const costControlSignals = [
        budget > 0 ? 100 : 0,
        committed > 0 ? 100 : 0,
        invoiced > 0 ? 100 : 0,
        (financial?.allocations || []).length > 0 ? 100 : 0,
        (financial?.incomes || []).length > 0 ? 100 : 0,
        // CPI de obra (EVM): 1,0 o más = 100; bajo 1 penaliza proporcionalmente.
        ...(constructionEvm && constructionEvm.cpi != null ? [Math.min(100, constructionEvm.cpi * 100)] : [])
      ];
      const costControlProgress = clampPercent(average(costControlSignals));

      const sectionProgress = {
        development: developmentProgress,
        operations: operationsProgress,
        project_management: projectManagementProgress,
        procurement: procurementProgress,
        construction: constructionProgress,
        cost_control: costControlProgress
      };

      const weights = {
        development: 30,
        operations: 20,
        project_management: 20,
        procurement: 15,
        construction: 10,
        cost_control: 5
      };

      const overallProgress = clampPercent(
        Object.entries(weights).reduce((sum, [key, weight]) => sum + weight * (sectionProgress[key] || 0), 0) /
          100
      );

      return {
        parkId: park.id,
        parkName: park.name,
        overallProgress,
        blockers: {
          criticalAlerts,
          overdueTasks,
          pendingOrders: pendingOrdersForPark,
          openRequisitions,
          openTenders,
          pendingShipments: shipmentsForPark.length - receivedShipments,
          expiredCerts
        },
        financial: {
          budget,
          committed,
          invoiced,
          budgetUsage
        },
        development: {
          stage: renewableProject?.stage || null,
          rtbProgress: renewableProject?.rtbProgress ?? null,
          permitsCount: permitsForProject.length,
          permitsAvg: clampPercent(permitsAvg),
          criticalAlerts
        },
        project_management: {
          timelineProgress,
          overdueTasks,
          baselineStart: timeline?.baselineStart || null,
          baselineEnd: timeline?.baselineEnd || null
        },
        procurement: {
          openRequisitions,
          convertedRequisitions,
          openTenders,
          pendingOrdersForPark,
          approvedOrdersForPark
        },
        operations: {
          shipmentsOpen: shipmentsForPark.length - receivedShipments,
          shipmentsTotal: shipmentsForPark.length
        },
        construction: {
          constructionProgress,
          overdueTasks: constructionTasks.filter((task) => {
            if (!task?.end) return false;
            const end = parseDate(task.end);
            return end && end < today && Number(task.progress || 0) < 100;
          }).length,
          tasksCount: constructionTasks.length,
          fromModule: hasConstructionData,
          plannedProgress: constructionEvm ? clampPercent(constructionEvm.plannedProgress) : null,
          cpi: constructionEvm?.cpi ?? null,
          spi: constructionEvm?.spi ?? null,
          alerts: hasConstructionData ? constructionSummary.alerts.length : 0
        },
        cost_control: {
          dataCompleteness,
          budgetUsage,
          constructionCpi: constructionEvm?.cpi ?? null
        },
        sectionProgress
      };
    });
  }, [
    executiveParks,
    renewableProjects,
    renewablePermits,
    renewablePermitAlerts,
    projectTimelines,
    purchaseRequisitions,
    purchaseOrders,
    tenders,
    suppliers,
    shipments,
    projectFinancialOverview,
    displayCurrency,
    fxUsdClp,
    findAreaById,
    constructionEnabled,
    getSummary
  ]);

  const selectedProject = useMemo(() => {
    if (!executiveProjects.length) return null;
    if (selectedParkId === 'all') return null;
    return executiveProjects.find((project) => project.parkId === selectedParkId) || null;
  }, [executiveProjects, selectedParkId]);

  const pendingTasks = useMemo(() => {
    const sum = (key) => executiveProjects.reduce((total, project) => total + (project.blockers[key] || 0), 0);
    const items = [
      { id: 'criticalAlerts', label: t('Alertas críticas de permisos/PAS'), count: sum('criticalAlerts'), icon: AlertTriangle, page: 'development-alerts' },
      { id: 'overdueTasks', label: t('Tareas de cronograma vencidas'), count: sum('overdueTasks'), icon: Calendar, page: 'projects-timeline' },
      { id: 'pendingOrders', label: t('Órdenes de compra pendientes de aprobación'), count: sum('pendingOrders'), icon: ShoppingCart, page: 'procurement-orders' },
      { id: 'openRequisitions', label: t('Solicitudes de pedido abiertas'), count: sum('openRequisitions'), icon: ClipboardList, page: 'procurement-requisitions' },
      { id: 'openTenders', label: t('Licitaciones abiertas'), count: sum('openTenders'), icon: Gavel, page: 'procurement-tenders' },
      { id: 'expiredCerts', label: t('Certificaciones de proveedores vencidas'), count: sum('expiredCerts'), icon: ShieldCheck, page: 'suppliers-compliance' }
    ];
    return items.filter((item) => item.count > 0).sort((a, b) => b.count - a.count);
  }, [executiveProjects]);

  const toggleSection = (sectionId) => {
    const current = new Set(selectedSections);
    if (current.has(sectionId)) {
      current.delete(sectionId);
    } else {
      current.add(sectionId);
    }
    updatePreferences({
      dashboardExecutive: {
        ...dashboardExecutive,
        sections: Array.from(current)
      }
    });
  };

  const ProgressRing = ({ value, label }) => {
    const pct = clampPercent(value);
    const color =
      pct === 0
        ? '#9aa4af'
        : pct < 50
        ? '#b7791f'
        : pct < 100
        ? '#2f6fb8'
        : '#1f9d55';
    return (
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: `conic-gradient(${color} ${pct}%, #dfe3e7 0)` }}
          aria-label={t(label) || t('Avance {0}%', pct)}
        >
          <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
            <span className="text-[10px] font-semibold text-neutral-700">{t(pct)}%</span>
          </div>
        </div>
      </div>
    );
  };

  const topLocations = useMemo(() => {
    return locations
      .map((location) => {
        const totalQuantity = inventory.reduce((sum, item) => {
          const stock = item.locations.find((loc) => loc.locationId === location.id);
          return sum + (stock ? stock.quantity : 0);
        }, 0);
        return { ...location, totalQuantity };
      })
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .slice(0, 4);
  }, [inventory, locations]);

  const recentOrders = ordersSource
    .slice()
    .sort((a, b) => parseDate(b.createdDate) - parseDate(a.createdDate))
    .slice(0, 6);

  const mapOrderToRow = (order) => {
    const area = order.areaId ? findAreaById(order.areaId) : null;
    const park = order.parkId ? findParkById(order.parkId) : area ? { id: area.parkId, name: area.parkName } : null;
    const costCenter = order.costCenterId ? findCostCenterById(order.costCenterId) : null;
    return {
      ...order,
      parkName: park?.name || '-',
      areaName: area?.name || '-',
      costCenterCode: costCenter?.code || '-',
      costCenterName: costCenter?.name || costCenter?.code || '-'
    };
  };

  const dashboardOrders = recentOrders.map(mapOrderToRow);

  const officeOrderRows = useMemo(() => {
    if (!isOfficeStaff) return [];
    return visibleOrders
      .slice()
      .sort((a, b) => parseDate(b.createdDate) - parseDate(a.createdDate))
      .map(mapOrderToRow);
  }, [isOfficeStaff, visibleOrders]);

  const recentMovements = movements
    .slice()
    .sort((a, b) => parseDate(b.date) - parseDate(a.date))
    .slice(0, 6);

  const movementRows = recentMovements.map((movement) => {
    const area = movement.areaId ? findAreaById(movement.areaId) : null;
    const park = movement.parkId ? findParkById(movement.parkId) : area ? { id: area.parkId, name: area.parkName } : null;
    const costCenter = movement.costCenterId ? findCostCenterById(movement.costCenterId) : null;
    return {
      ...movement,
      parkName: park?.name || '-',
      areaName: area?.name || '-',
      costCenterCode: costCenter?.code || '-'
    };
  });

  const orderColumns = [
    { key: 'id', label: t('N° OC'), sortable: true },
    {
      key: 'supplierName',
      label: t('Proveedor'),
      sortable: true,
      render: (value) => <span className="font-medium text-neutral-800">{t(value)}</span>
    },
    {
      key: 'type',
      label: t('Tipo'),
      sortable: true,
      render: (type) => (
        <Badge tone="neutral">{type === 'internacional' ? t('Internacional') : t('Nacional')}</Badge>
      )
    },
    {
      key: 'totalValue',
      label: t('Valor Total'),
      sortable: true,
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'parkName',
      label: t('Parque'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'areaName',
      label: t('Área'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'costCenterCode',
      label: t('Centro Costo'),
      sortable: true,
      render: (_, row) => (
        <div>
          <p className="font-medium text-neutral-800">{t(row.costCenterCode) || '-'}</p>
          <p className="text-xs text-neutral-400">{t(row.costCenterName) || ''}</p>
        </div>
      )
    },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (status) => <Badge tone={getStatusTone(status)}>{t(getStatusText(status))}</Badge>
    },
    {
      key: 'createdDate',
      label: t('Fecha creación'),
      sortable: true,
      render: (date) => formatDate(date)
    }
  ];

  if (isOfficeStaff) {
    const officePendingRequisitions = requisitionsByUser.filter((req) =>
      ['pendiente_area', 'pendiente_controller', 'pendiente_compra'].includes(req.status)
    ).length;

    const officeRequisitionRows = requisitionsByUser
      .slice()
      .sort((a, b) => parseDate(b.createdDate) - parseDate(a.createdDate))
      .map((req) => {
        const park = req.parkId ? findParkById(req.parkId) : null;
        const area = req.areaId ? findAreaById(req.areaId) : null;
        return {
          id: req.id,
          title: req.title,
          parkName: park?.name || '-',
          areaName: area?.name || '-',
          neededDate: req.neededDate,
          status: req.status,
          routingCode: req.routingCode || '-',
          createdDate: req.createdDate
        };
      });

    const officeRequisitionColumns = [
      { key: 'id', label: t('N° Solicitud'), sortable: true },
      { key: 'title', label: t('Título'), sortable: true },
      {
        key: 'parkName',
        label: t('Parque'),
        sortable: true
      },
      {
        key: 'neededDate',
        label: t('Fecha requerida'),
        sortable: true,
        render: (value) => formatDate(value)
      },
      {
        key: 'routingCode',
        label: t('Código Parque'),
        sortable: true
      },
      {
        key: 'status',
        label: t('Estado'),
        sortable: true,
        render: (status) => <Badge tone={getStatusTone(status)}>{t(getStatusText(status))}</Badge>
      }
    ];

    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-neutral-900">{t('Resumen de Solicitudes y Órdenes')}</h1>
            <p className="text-sm text-neutral-500">{t('Órdenes correspondientes a tus proyectos y solicitudes generadas.')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            title={t('Órdenes vinculadas')}
            value={totalOrders.toString()}
            subtitle={t('Generadas para tus proyectos')}
            icon={ShoppingCart}
            onClick={() => setActivePage && setActivePage('procurement-orders')}
          />
          <StatCard
            title={t('Órdenes pendientes')}
            value={pendingOrders.toString()}
            subtitle={t('En proceso de aprobación')}
            icon={AlertTriangle}
            onClick={() => setActivePage && setActivePage('procurement-orders')}
          />
          <StatCard
            title={t('Mis solicitudes')}
            value={requisitionsByUser.length.toString()}
            subtitle={t('Total emitidas')}
            icon={ClipboardList}
            onClick={() => setActivePage && setActivePage('procurement-requisitions')}
          />
          <StatCard
            title={t('Solicitudes pendientes')}
            value={officePendingRequisitions.toString()}
            subtitle={t('En revisión o proceso de compra')}
            icon={Package}
            onClick={() => setActivePage && setActivePage('procurement-requisitions')}
          />
        </div>

        <DataTable
          data={officeOrderRows}
          columns={orderColumns}
          searchPlaceholder={t('Buscar por OC, proveedor o parque...')}
        />

        <DataTable
          data={officeRequisitionRows}
          columns={officeRequisitionColumns}
          searchPlaceholder={t('Buscar solicitudes por título o parque...')}
        />
      </div>
    );
  }

  const movementColumns = [
    { key: 'reference', label: t('Referencia'), sortable: true },
    { key: 'sku', label: 'SKU', sortable: true },
    {
      key: 'type',
      label: t('Movimiento'),
      sortable: true,
      render: (type) => <Badge tone="neutral"><span className="capitalize">{t(type)}</span></Badge>
    },
    {
      key: 'quantity',
      label: t('Cantidad'),
      sortable: true,
      align: 'right',
      render: (quantity) => <span className="font-medium text-neutral-800">{t(quantity)}</span>
    },
    {
      key: 'locationId',
      label: t('Ubicación'),
      render: (locationId) => locations.find((location) => location.id === locationId)?.name || '-'
    },
    {
      key: 'parkName',
      label: t('Parque'),
      render: (value) => value || '-'
    },
    {
      key: 'costCenterCode',
      label: t('Centro Costo'),
      render: (value) => value || '-'
    },
    {
      key: 'date',
      label: t('Fecha'),
      sortable: true,
      render: (date) => formatDate(date)
    }
  ];

  const totalBudget = projectFinancialOverview.reduce(
    (sum, summary) => sum + convertAmount(summary.totalBudget || 0, summary.currency || 'USD', displayCurrency),
    0
  );
  const totalCommitted = projectFinancialOverview.reduce(
    (sum, summary) => sum + convertAmount(summary.totalCommitted || 0, summary.currency || 'USD', displayCurrency),
    0
  );
  const totalInvoiced = projectFinancialOverview.reduce(
    (sum, summary) => sum + convertAmount(summary.totalInvoiced || 0, summary.currency || 'USD', displayCurrency),
    0
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">{t('Resumen Ejecutivo')}</h1>
          <p className="text-sm text-neutral-500">{t(
            'Estado general de proyectos: desarrollo, control, abastecimiento, proveedores, logística y reportería.'
          )}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1.5 rounded border border-neutral-200 bg-white text-neutral-600 font-medium">{t('Compras nacionales:')} <span className="num font-semibold text-neutral-800">{formatDisplayMoney(monthlyNational)}</span>
          </span>
          <span className="px-2.5 py-1.5 rounded border border-neutral-200 bg-white text-neutral-600 font-medium">{t('Compras internacionales:')} <span className="num font-semibold text-neutral-800">{formatDisplayMoney(monthlyInternational)}</span>
          </span>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded p-4">
        <h2 className="text-sm font-semibold text-neutral-800 mb-3">{t('Pendientes que requieren atención')}</h2>
        {pendingTasks.length === 0 ? (
          <p className="text-sm text-neutral-500">{t('Sin pendientes críticos en el portafolio visible.')}</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {pendingTasks.map((task) => {
              const Icon = task.icon;
              return (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() => setActivePage && setActivePage(task.page)}
                    className="w-full flex items-center justify-between gap-3 py-2 text-left hover:bg-neutral-50 -mx-1 px-1 rounded"
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <Icon className="w-4 h-4 text-neutral-400 shrink-0" />
                      <span className="text-sm text-neutral-700 truncate">{t(task.label)}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="num text-sm font-semibold text-neutral-900">{t(task.count)}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-neutral-400" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-neutral-800">{t('Resumen por proyecto')}</h2>
            <p className="text-xs text-neutral-500">{t(
              'Selecciona un proyecto y elige qué áreas mostrar en el resumen ejecutivo.'
            )}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500">{t('Proyecto')}</span>
              <select
                value={selectedParkId}
                onChange={(event) =>
                  updatePreferences({
                    dashboardExecutive: {
                      ...dashboardExecutive,
                      parkId: event.target.value
                    }
                  })
                }
                className="h-8 px-2.5 border border-neutral-300 rounded bg-white text-sm"
              >
                <option value="all">{t('Todos')}</option>
                {executiveParks.map((park) => (
                  <option key={park.id} value={park.id}>
                    {t(park.name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500">{t('Moneda')}</span>
              <select
                value={displayCurrency}
                onChange={(event) => updatePreferences({ displayCurrency: event.target.value })}
                className="h-8 px-2.5 border border-neutral-300 rounded bg-white text-sm"
              >
                <option value="USD">{t('USD')}</option>
                <option value="CLP">{t('CLP')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {sectionCatalog.map((section) => {
            const enabled = selectedSections.has(section.id);
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => toggleSection(section.id)}
                className={`h-7 px-2.5 rounded border text-xs font-medium transition-colors ${
                  enabled
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                {t(section.label)}· {t(section.weight)}%
                              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {executiveProjects.map((project) => (
            <button
              key={project.parkId}
              type="button"
              onClick={() =>
                updatePreferences({
                  dashboardExecutive: {
                    ...dashboardExecutive,
                    parkId: project.parkId
                  }
                })
              }
              className={`text-left bg-neutral-50 border rounded p-4 hover:bg-neutral-100 transition-colors ${
                selectedParkId === project.parkId ? 'border-brand-500' : 'border-neutral-200'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-neutral-900">{t(project.parkName)}</p>
                  <p className="text-xs text-neutral-500">{t('Pendientes:')} {project.blockers.pendingOrders + project.blockers.openRequisitions + project.blockers.openTenders}
                    {project.blockers.criticalAlerts > 0 ? t(' · Alertas: {0}', project.blockers.criticalAlerts) : ''}
                  </p>
                </div>
                <ProgressRing value={project.overallProgress} label={t('Avance total {0}', project.parkName)} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-white border border-neutral-200 rounded p-2.5">
                  <p className="text-[11px] text-neutral-500 font-medium">{t('Presupuesto')}</p>
                  <p className="num text-left text-sm font-semibold text-neutral-900">{formatDisplayMoney(project.financial.budget)}</p>
                </div>
                <div className="bg-white border border-neutral-200 rounded p-2.5">
                  <p className="text-[11px] text-neutral-500 font-medium">{t('Comprometido')}</p>
                  <p className="num text-left text-sm font-semibold text-neutral-900">{formatDisplayMoney(project.financial.committed)}</p>
                </div>
                <div className="bg-white border border-neutral-200 rounded p-2.5">
                  <p className="text-[11px] text-neutral-500 font-medium">{t('Uso')}</p>
                  <p className="num text-left text-sm font-semibold text-neutral-900">{t(project.financial.budgetUsage)}%</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {selectedProject && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {sectionCatalog
              .filter((section) => selectedSections.has(section.id))
              .map((section) => {
                const Icon = section.icon;
                const progress = selectedProject.sectionProgress[section.id] || 0;

                const details =
                  section.id === 'development'
                    ? [
                        `Etapa: ${selectedProject.development.stage || '—'}`,
                        `Permisos: ${selectedProject.development.permitsCount} · Avg ${selectedProject.development.permitsAvg}%`,
                        `Alertas críticas: ${selectedProject.development.criticalAlerts}`
                      ]
                    : section.id === 'operations'
                    ? [
                        `Despachos abiertos: ${selectedProject.operations.shipmentsOpen}`,
                        `Despachos totales: ${selectedProject.operations.shipmentsTotal}`,
                        `Recepción: ${selectedProject.sectionProgress.operations}%`
                      ]
                    : section.id === 'project_management'
                    ? [
                        `Cronograma: ${selectedProject.project_management.timelineProgress}%`,
                        `Tareas vencidas: ${selectedProject.project_management.overdueTasks}`,
                        `Ventana: ${selectedProject.project_management.baselineStart || '—'} → ${selectedProject.project_management.baselineEnd || '—'}`
                      ]
                    : section.id === 'procurement'
                    ? [
                        `PR abiertas: ${selectedProject.procurement.openRequisitions} · Convertidas: ${selectedProject.procurement.convertedRequisitions}`,
                        `RFQ/Licitaciones abiertas: ${selectedProject.procurement.openTenders}`,
                        `OC pendientes aprobación: ${selectedProject.procurement.pendingOrdersForPark}`
                      ]
                    : section.id === 'construction' && selectedProject.construction.fromModule
                    ? [
                        t('Avance obra: {0}% · programado {1}%', selectedProject.construction.constructionProgress, selectedProject.construction.plannedProgress),
                        t('CPI {0} · SPI {1}', formatIndex(selectedProject.construction.cpi), formatIndex(selectedProject.construction.spi)),
                        t('Fuente: módulo Construcción (EVM) · {0} alertas', selectedProject.construction.alerts)
                      ]
                    : section.id === 'construction'
                    ? [
                        `Avance: ${selectedProject.construction.constructionProgress}% · Tareas: ${selectedProject.construction.tasksCount}`,
                        `Tareas vencidas: ${selectedProject.construction.overdueTasks}`,
                        `Base: cronograma del proyecto`
                      ]
                    : section.id === 'cost_control'
                    ? [
                        `Uso presupuesto (comprometido): ${selectedProject.cost_control.budgetUsage}%`,
                        `Data disponible: ${selectedProject.cost_control.dataCompleteness}%`,
                        `Moneda: ${displayCurrency} · FX ${fxUsdClp}`,
                        ...(selectedProject.cost_control.constructionCpi != null
                          ? [t('CPI de obra: {0}', formatIndex(selectedProject.cost_control.constructionCpi))]
                          : [])
                      ]
                    : ['Resumen no disponible.'];

                return (
                  <div key={section.id} className="border border-neutral-200 rounded p-4 bg-neutral-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-neutral-500 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-neutral-900">{t(section.label)}</p>
                          <p className="text-xs text-neutral-500">{t('Peso')} {t(section.weight)}%</p>
                        </div>
                      </div>
                      <ProgressRing value={progress} label={`${section.label} ${progress}%`} />
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {details.map((line) => (
                        <li key={line} className="text-xs text-neutral-600">
                          {t(line)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6">
        <StatCard
          title={t('Proyectos activos')}
          value={executiveParks.length.toString()}
          subtitle={t('Portafolio visible')}
          icon={Leaf}
          onClick={() => setActivePage && setActivePage('development-portfolio')}
        />
        <StatCard
          title={t('Avance promedio')}
          value={`${clampPercent(average(executiveProjects.map((p) => p.overallProgress)))}%`}
          subtitle={t('Promedio portafolio')}
          icon={AlertTriangle}
          onClick={() => setActivePage && setActivePage('dashboard')}
        />
        <StatCard
          title={t('Alertas críticas')}
          value={executiveProjects.reduce((sum, p) => sum + (p.blockers.criticalAlerts || 0), 0).toString()}
          subtitle={t('Permisos/PAS vencidos')}
          icon={AlertTriangle}
          onClick={() => setActivePage && setActivePage('development-alerts')}
        />
        <StatCard
          title={t('Tareas vencidas')}
          value={executiveProjects.reduce((sum, p) => sum + (p.blockers.overdueTasks || 0), 0).toString()}
          subtitle={t('Cronograma atrasado')}
          icon={Calendar}
          onClick={() => setActivePage && setActivePage('projects-timeline')}
        />
        <StatCard
          title={t('Compras mes')}
          value={formatDisplayMoney(monthlyNational + monthlyInternational)}
          subtitle={t('Nacional + internacional')}
          icon={ShoppingCart}
          onClick={() => setActivePage && setActivePage('reports')}
        />
        <StatCard
          title={t('Presupuesto / Comprometido')}
          value={`${formatDisplayMoney(totalBudget)} / ${formatDisplayMoney(totalCommitted)}`}
          subtitle={t('Control de costos')}
          icon={BarChart3}
          onClick={() => setActivePage && setActivePage('reports')}
        />
      </div>

      {constructionEnabled && constructionPortfolio.projects > 0 && (
        <div className="bg-white border border-neutral-200 rounded p-4">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <HardHat className="w-4 h-4 text-neutral-500" />
              <div>
                <h2 className="text-sm font-semibold text-neutral-800">{t('Construcción')}</h2>
                <p className="text-xs text-neutral-500">{t('Avance físico y desempeño EVM de los proyectos en obra.')}</p>
              </div>
            </div>
            <button type="button" onClick={() => setActivePage && setActivePage('construction-costs')} className="text-xs font-medium text-brand-700 hover:underline">
              {t('Ver control de costos')}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <StatCard title={t('% avance construcción')} value={`${constructionPortfolio.actualProgress.toFixed(1)}%`} subtitle={t('Programado {0}%', constructionPortfolio.plannedProgress.toFixed(1))} icon={HardHat} onClick={() => setActivePage && setActivePage('construction-progress')} />
            <StatCard title={t('CPI portafolio')} value={formatIndex(constructionPortfolio.cpi)} subtitle={t('EV / AC')} icon={BarChart3} onClick={() => setActivePage && setActivePage('construction-costs')} />
            <StatCard title={t('SPI portafolio')} value={formatIndex(constructionPortfolio.spi)} subtitle={t('EV / PV')} icon={Calendar} onClick={() => setActivePage && setActivePage('construction-progress')} />
            <StatCard title={t('EAC vs BAC')} value={formatDisplayMoney(constructionPortfolio.cpi ? constructionPortfolio.bac / constructionPortfolio.cpi : constructionPortfolio.bac)} subtitle={t('BAC {0}', formatDisplayMoney(constructionPortfolio.bac))} icon={Wrench} onClick={() => setActivePage && setActivePage('construction-costs')} />
            <StatCard title={t('Alertas de obra')} value={String(constructionAlerts.length)} subtitle={t('SPI, CPI, hitos, NC, materiales')} icon={AlertTriangle} onClick={() => setActivePage && setActivePage('development-alerts')} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-100">
                  <th className="py-1.5 pr-3 font-medium">{t('Proyecto')}</th>
                  <th className="py-1.5 pr-3 font-medium text-right">{t('% avance construcción')}</th>
                  <th className="py-1.5 pr-3 font-medium text-right">{t('Programado')}</th>
                  <th className="py-1.5 pr-3 font-medium text-right">{t('% avance RTB')}</th>
                  <th className="py-1.5 pr-3 font-medium text-right">CPI</th>
                  <th className="py-1.5 pr-3 font-medium text-right">SPI</th>
                </tr>
              </thead>
              <tbody>
                {[...constructionSummaries.values()].filter((summary) => summary.items.length > 0).map((summary) => {
                  const p = summary.evm.project;
                  const tone = (value) => (value == null ? '' : value >= 1 ? 'text-success-700' : value >= 0.9 ? 'text-warning-700' : 'text-danger-700');
                  return (
                    <tr key={summary.parkId} className="border-b border-neutral-50">
                      <td className="py-1.5 pr-3">{summary.parkName}</td>
                      <td className="py-1.5 pr-3 text-right num">{p.actualProgress.toFixed(1)}%</td>
                      <td className="py-1.5 pr-3 text-right num">{p.plannedProgress.toFixed(1)}%</td>
                      <td className="py-1.5 pr-3 text-right num">{summary.gate.renewable?.rtbProgress ?? '—'}%</td>
                      <td className={`py-1.5 pr-3 text-right num font-medium ${tone(p.cpi)}`}>{formatIndex(p.cpi)}</td>
                      <td className={`py-1.5 pr-3 text-right num font-medium ${tone(p.spi)}`}>{formatIndex(p.spi)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <ChartCard className="xl:col-span-2" title={t('Compras mensuales ({0})', displayCurrency)}>
          <Bar
            data={monthlyTrend}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'bottom',
                  labels: {
                    font: { size: 12 }
                  }
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatDisplayMoney(context.parsed.y)}`
                  }
                }
              },
              scales: {
                y: {
                  ticks: {
                    callback: (value) => formatDisplayMoney(value)
                  },
                  grid: { color: '#E2E8F0' }
                },
                x: {
                  grid: { display: false }
                }
              }
            }}
          />
        </ChartCard>

        <ChartCard title={t('Stock por locación')}>
          <div className="space-y-3">
            {topLocations.map((location) => (
              <div
                key={location.id}
                className="bg-neutral-50 border border-neutral-200 rounded p-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-800">{t(location.name)}</p>
                    <p className="text-xs text-neutral-500">{t(location.code)}</p>
                  </div>
                  <span className="num text-sm font-medium text-neutral-800">
                    {t(location.totalQuantity)} {t('unidades')}</span>
                </div>
                <div className="mt-2.5 h-1.5 bg-white rounded-full overflow-hidden border border-neutral-200">
                  <div
                    className="h-full bg-brand-600"
                    style={{
                      width: `${Math.min(location.totalQuantity / 4, 100)}%`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title={t('Compliance proveedores')}>
          <div className="flex flex-col justify-center h-full">
            <div className="mx-auto text-center">
              <div
                className={`w-32 h-32 rounded-full border-8 flex items-center justify-center ${
                  averageCompliance >= 85
                    ? 'border-success-200'
                    : averageCompliance >= 60
                    ? 'border-info-200'
                    : 'border-warning-200'
                }`}
              >
                <span
                  className={`text-2xl font-semibold ${
                    averageCompliance >= 85
                      ? 'text-success-600'
                      : averageCompliance >= 60
                      ? 'text-info-600'
                      : 'text-warning-600'
                  }`}
                >
                  {t(averageCompliance)}%
                </span>
              </div>
              <p className="mt-3 text-sm text-neutral-500">{t('Promedio de cumplimiento en due diligence para los proveedores activos.')}</p>
            </div>
          </div>
        </ChartCard>

        <ChartCard title={t('Estado de envíos a terreno')}>
          <div className="space-y-3">
            {shipments.slice(0, 4).map((shipment) => (
              <div
                key={shipment.id}
                className="p-3 rounded border border-neutral-200 bg-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-800">{t(shipment.reference)}</p>
                    <p className="text-xs text-neutral-500">
                      {t(locations.find((loc) => loc.id === shipment.destinationLocationId)?.name) || ''}
                    </p>
                  </div>
                  <Badge tone={shipment.status === 'recepcionado' ? 'success' : 'info'}>
                    {t(shipment.status)}
                  </Badge>
                </div>
                <p className="text-xs text-neutral-400 mt-1.5">{t('Responsable:')} {t(shipment.responsible)} {t('· Ítems:')} {shipment.items.reduce((sum, item) => sum + item.quantity, 0)}
                </p>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      <ChartCard title={t('Presupuesto por Parque')}>
        <div className="space-y-3">
          {projectFinancialOverview.map((summary) => (
            <div key={summary.parkId} className="p-3 bg-neutral-50 rounded border border-neutral-200 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-800">{t(summary.parkName)}</p>
                  <p className="text-xs text-neutral-500">{t('Presupuesto:')} {formatCurrency(summary.totalBudget)}</p>
                </div>
                <div className="text-right text-xs text-neutral-500">
                  <p>{t('Comprometido:')} {formatCurrency(summary.totalCommitted)}</p>
                  <p>{t('Facturado:')} {formatCurrency(summary.totalInvoiced)}</p>
                </div>
              </div>
              <div className="space-y-2">
                {summary.allocations.map((allocation) => {
                  const base = allocation.amount || 1;
                  const progress = Math.min((allocation.committed / base) * 100, 100);
                  const invoiceProgress = Math.min((allocation.invoiced / base) * 100, 100);
                  return (
                    <div key={allocation.id}>
                      <div className="flex items-center justify-between text-xs text-neutral-500">
                        <span>{t(allocation.name)}</span>
                        <span>{formatCurrency(allocation.committed)} / {formatCurrency(allocation.amount)}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-white rounded-full border border-neutral-200 overflow-hidden">
                        <div className="h-full bg-neutral-600" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="mt-1 h-1 bg-white rounded-full border border-neutral-200 overflow-hidden">
                        <div className="h-full bg-success-500" style={{ width: `${invoiceProgress}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-neutral-500">{t('Disponible:')} {formatCurrency(summary.available)}</p>
            </div>
          ))}
        </div>
      </ChartCard>

      <DataTable
        data={dashboardOrders}
        columns={orderColumns}
        searchPlaceholder={t('Buscar por OC, proveedor...')}
      />

      <DataTable
        data={movementRows}
        columns={movementColumns}
        searchPlaceholder={t('Buscar movimientos por SKU, referencia...')}
      />
    </div>
  );
};

export default Dashboard;
